import { DAY_MS, dueDayMs, etDayMs, parseDateToMs, todayBusinessMs } from '@/utils/date-utils';

/**
 * Split tasks into Overdue / Today by their due BUSINESS DAY against ET today — never by comparing
 * the raw `dueDate` to viewer-local midnight, which files every midnight-UTC marker due today
 * under Overdue for a US viewer (00:00Z is the previous evening there). Each list is sorted by due
 * day; tasks due later, or with no due date, are in neither.
 */
export function bucketTasksByDueDay<T extends { dueDate?: unknown }>(
	tasks: T[],
	todayMs: number = todayBusinessMs(),
): { overdue: T[]; today: T[] } {
	const dated = tasks
		.map((task) => ({ task, day: dueDayMs(task.dueDate) }))
		.filter((d): d is { task: T; day: number } => d.day !== null)
		.sort((a, b) => a.day - b.day);
	return {
		overdue: dated.filter((d) => d.day < todayMs).map((d) => d.task),
		today: dated.filter((d) => d.day === todayMs).map((d) => d.task),
	};
}

/** Whole business days a due date is past ET today: 0 for today or later, null for no date. */
export function daysOverdue(dueDate: unknown, todayMs: number = todayBusinessMs()): number | null {
	const day = dueDayMs(dueDate);
	return day === null ? null : Math.max(0, Math.round((todayMs - day) / DAY_MS));
}

/**
 * A work order's due-date state, in whole ET business days. The due date is a business day
 * (`dueDayMs`); the `createdAt` fallback is a real moment, so it is converted with `etDayMs`
 * before it is compared with a marker. Comparing either to `Date.now()` is off by the ET offset:
 * after 8 p.m. ET tomorrow's marker is already in the past.
 */
export function workOrderDueAge(
	dueDate: unknown,
	createdAt: unknown,
	todayMs: number = todayBusinessMs(),
): { dueDay: number | null; isFutureDue: boolean; ageInDays: number } {
	const dueDay = dueDayMs(dueDate);
	const createdMs = parseDateToMs(createdAt);
	const basis = dueDay ?? (createdMs === null ? null : etDayMs(createdMs));
	return {
		dueDay,
		isFutureDue: dueDay !== null && dueDay > todayMs,
		ageInDays: basis === null ? 0 : Math.round((todayMs - basis) / DAY_MS),
	};
}
